import {
  AuthChangeEvent,
  Session,
  AuthResponse,
  UserResponse,
  AuthError,
} from '@supabase/supabase-js';
import { supabase } from '../../lib/supabase';

export const authService = {
  async getSession(): Promise<
    | { data: { session: Session | null }; error: null }
    | { data: { session: null }; error: AuthError }
  > {
    return await supabase.auth.getSession();
  },

  async signIn(email: string, password: string): Promise<AuthResponse> {
    return await supabase.auth.signInWithPassword({ email, password });
  },

  async signUp(
    email: string,
    password: string,
    fullName: string,
    termsAcceptedAt?: string,
    birthYear?: number
  ): Promise<AuthResponse> {
    return await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          ...(termsAcceptedAt && { terms_accepted_at: termsAcceptedAt }),
          ...(birthYear && { birth_year: birthYear }),
        },
      },
    });
  },

  async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    return await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
  },

  // Fix: Changed return type from AuthResponse to UserResponse because updateUser returns data: { user: User } and not session data
  async updatePassword(password: string): Promise<UserResponse> {
    return await supabase.auth.updateUser({ password });
  },

  async deleteAccount(): Promise<{ error: Error | null }> {
    try {
      const { error } = await supabase.rpc('delete_account');
      if (error) throw error;
      return { error: null };
    } catch (err) {
      return { error: err as Error };
    }
  },

  async signOut(): Promise<{ error: AuthError | null }> {
    return await supabase.auth.signOut();
  },

  /**
   * Accept a pending athlete invite after signup.
   * Looks up the invite token, validates it, creates the team_connections row,
   * and stamps accepted_at on the invite. Safe to call even if token is expired or
   * already used — returns { accepted: false, reason } in those cases.
   */
  async acceptAthleteInvite(
    token: string,
    athleteId: string
  ): Promise<{ accepted: boolean; reason?: string }> {
    const { data, error } = await supabase
      .rpc('accept_athlete_invite', { p_token: token, p_athlete_id: athleteId });

    if (error) return { accepted: false, reason: 'db_error' };
    return (data as { accepted: boolean; reason?: string }) ?? { accepted: false, reason: 'unknown' };
  },

  /**
   * Fetch enough invite metadata to show the "Invited by Coach <name>" chip
   * on the signup form. Returns null if token is invalid or expired.
   */
  async getInvitePreview(
    token: string
  ): Promise<{ coachName: string; coachSchool: string } | null> {
    const { data, error } = await supabase
      .rpc('get_invite_preview', { p_token: token });

    if (error || !data || data.length === 0) return null;
    const row = data[0];
    if (!row.is_valid) return null;
    return { coachName: row.coach_name ?? '', coachSchool: row.coach_school ?? '' };
  },

  onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void) {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      callback(event, session);
    });
    return subscription;
  },
};
